import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactTooltip from 'react-tooltip';
import Toggle from 'react-toggle';
import StepHeader from '../StepHeader/StepHeader';
import ShipsInfo from '../ShipsInfo/ShipsInfo';
import NumberFormat from 'react-number-format';
import Checkbox from '../Form/Checkbox/Checkbox';
import Loading from '../Loading/Loading';
import { validateStep } from '../../utils/orderFormUtils';
import CrossSells from '../CrossSells/CrossSells';
import { states } from '../ShippingInfo/states';
import { errorMessages } from './errorMessages';
import { isExpDateValid } from '../../utils/orderFormUtils';
import { buildQueryParams } from '../../utils/helpers';
import { Context as OrderContext } from '../../context/OrderContext';
import cvvImage from './cvv.jpeg';
import shieldImage from './shield.webp';
import './PaymentInfo.sass';
import 'react-toggle/style.css';

const months = [
	{ label: 'Jan (01)', value: '01' },
	{ label: 'Feb (02)', value: '02' },
	{ label: 'Mar (03)', value: '03' },
	{ label: 'Apr (04)', value: '04' },
	{ label: 'May (05)', value: '05' },
	{ label: 'Jun (06)', value: '06' },
	{ label: 'Jul (07)', value: '07' },
	{ label: 'Aug (08)', value: '08' },
	{ label: 'Sep (09)', value: '09' },
	{ label: 'Oct (10)', value: '10' },
	{ label: 'Nov (11)', value: '11' },
	{ label: 'Dec (12)', value: '12' },
];

const years = [
	{ label: '2024', value: '24' },
	{ label: '2025', value: '25' },
	{ label: '2026', value: '26' },
	{ label: '2027', value: '27' },
	{ label: '2028', value: '28' },
	{ label: '2029', value: '29' },
	{ label: '2030', value: '30' },
	{ label: '2031', value: '31' },
	{ label: '2032', value: '32' },
	{ label: '2033', value: '33' },
	{ label: '2034', value: '34' },
];

export default function PaymentInfo(props) {
	// eslint-disable-next-line no-restricted-globals
	const queryParams = buildQueryParams(location);
	const {
		onPressBack,
		onFieldUpdate,
		orderData,
		onTermsClick,
		onPrivacyClick,
		onSubmitStep,
		formErrors,
		isOneLineCheckout,
		webtvDisclaimer,
		onCrossSellAdd,
		isOneProductCheckout,
		crossSells,
		protectionPrice,
		onProtectionCheckboxChange,
	} = props;
	const [agreeTerms, setAgreeTerms] = useState(false);
	const [agreeTermsError, setAgreeTermsError] = useState(false);

	const [expMonth, setExpMonth] = useState('01');
	const [expYear, setExpYear] = useState(years[0].value);
	const [isBillingSame, setBillingSame] = useState(orderData.billingSameAsShipping);

	const showWebtvDisclaimer = queryParams.disclaimer === '1';

	const {
		state: { error, loading },
	} = useContext(OrderContext);

	function preparePaymentInfo(isPayPalOrder) {
		const billingInfo = {};
		let result = {};

		if (isBillingSame) {
			billingInfo.billingFirstName = orderData.firstName;
			billingInfo.billingLastName = orderData.lastName;
			billingInfo.billingAddress1 = orderData.shippingAddress1;
			billingInfo.billingCity = orderData.shippingCity;
			billingInfo.billingState = orderData.shippingState;
			billingInfo.billingCountry = orderData.shippingCountry;
			billingInfo.billingZip = orderData.shippingZip;
		}

		result = {
			...orderData,
			creditCardNumber: orderData.creditCardNumber.replaceAll(' ', ''),
			expirationDate: expMonth + expYear,
			billingSameAsShipping: isBillingSame,
			...billingInfo,
		};

		if (isPayPalOrder) {
			result.isPayPalOrder = true;
			result.alt_pay_return_url =
				window.location.origin + '/thank-you' + window.location.search + '&preloader=0';
		}

		return result;
	}

	function handleSubmit() {
		const errors = validateStep(
			orderData,
			isBillingSame ? errorMessages[0] : { ...errorMessages[0], ...errorMessages[1] }
		);

		if (!agreeTerms) {
			errors.agreeTerms = 'You must agree to terms and conditions if you want to proceed';
		} else {
			delete errors.agreeTerms;
		}

		if (!isExpDateValid(expMonth, expYear)) {
			errors.expDateError = 'Please provide valid expiration date';
		} else {
			delete errors.expDateError;
		}

		onSubmitStep(preparePaymentInfo(), errors, 4);
	}

	function renderCountrySelect() {
		return (
			<select
				value={orderData.billingCountry}
				placeholder='Country'
				name='billingCountry'
				onChange={onFieldUpdate}
				className={formErrors.billingCountry && 'error'}
			>
				<option value='US'>United States</option>
				<option value='CA'>Canada</option>
			</select>
		);
	}

	function renderStateSelect() {
		return (
			<select
				name='billingState'
				value={orderData.billingState}
				onChange={onFieldUpdate}
				placeholder='Select State/Province'
				className={formErrors.billingState && 'error'}
			>
				<option value='' disabled>
					State/Province
				</option>
				{Object.entries(states[orderData.billingCountry]).map(([abb, name], index) => (
					<option key={index} value={abb}>
						{name}
					</option>
				))}
			</select>
		);
	}

	function renderBillingForm() {
		const formBillingClassname = classNames({
			'form--billing': true,
			visible: !isBillingSame,
		});

		return (
			<div className='form form--noMargin'>
				<div className='form__header'>
					<Checkbox
						label='Billing same as shipping'
						checked={isBillingSame}
						onChange={() => setBillingSame(!isBillingSame)}
					/>
				</div>
				<div className={formBillingClassname}>
					<div className='form__group'>
						<div className='form__field'>
							<input
								type='text'
								placeholder='First Name'
								value={orderData.billingFirstName}
								name='billingFirstName'
								onChange={onFieldUpdate}
								className={formErrors.billingFirstName && 'error'}
							/>
							{formErrors.billingFirstName && (
								<div className='form__error'>{formErrors.billingFirstName}</div>
							)}
						</div>
						<div className='form__field'>
							<input
								type='text'
								placeholder='Last Name'
								value={orderData.billingLastName}
								name='billingLastName'
								onChange={onFieldUpdate}
								className={formErrors.billingLastName && 'error'}
							/>
							{formErrors.billingLastName && <div className='form__error'>{formErrors.billingLastName}</div>}
						</div>
					</div>
					<div className='form__field'>
						<input
							type='text'
							placeholder='Address'
							value={orderData.billingAddress1}
							name='billingAddress1'
							onChange={onFieldUpdate}
							className={formErrors.billingAddress1 && 'error'}
						/>
						{formErrors.billingAddress1 && <div className='form__error'>{formErrors.billingAddress1}</div>}
					</div>
					<div className='form__group'>
						<div className='form__field'>
							<input
								type='text'
								placeholder='City'
								value={orderData.billingCity}
								name='billingCity'
								onChange={onFieldUpdate}
								className={formErrors.billingCity && 'error'}
							/>
							{formErrors.billingCity && <div className='form__error'>{formErrors.billingCity}</div>}
						</div>
						<div className='form__field'>
							{renderStateSelect()}
							{formErrors.billingState && <div className='form__error'>{formErrors.billingState}</div>}
						</div>
					</div>
					<div className='form__group'>
						<div className='form__field'>
							{renderCountrySelect()}
							{formErrors.billingCountry && <div className='form__error'>{formErrors.billingCountry}</div>}
						</div>
						<div className='form__field'>
							<input
								type='text'
								placeholder='Zip/Postal'
								value={orderData.billingZip}
								name='billingZip'
								onChange={onFieldUpdate}
								className={formErrors.billingZip && 'error'}
							/>
							{formErrors.billingZip && <div className='form__error'>{formErrors.billingZip}</div>}
						</div>
					</div>
				</div>
			</div>
		);
	}

	function renderProtectionPackage() {
		if (!protectionPrice) {
			return null;
		}

		return (
			<div className='protection-package'>
				<div className='protection-package__left'>
					<img src={shieldImage} alt='protection-package' />
				</div>
				<div className='protection-package__right'>
					<div className='protection-package__title'>
						<p>Journey Package Protection</p>
						<label>
							<Toggle
								className='protection-package__toggle'
								icons={false}
								defaultChecked={orderData.upsellProductIds.includes('2560')}
								onChange={(e) => onProtectionCheckboxChange(e.target.checked)}
							/>
						</label>
					</div>
					<div className='protection-package__text'>
						{`100% Guaranteed Package Protection From Damage, Loss, & Theft For Just $${protectionPrice}/item. In the
						event your delivery is damaged, stolen, or lost during transit, we will replace it 100% free. No
						questions asked. Protect your package and your family today!`}
					</div>
				</div>
			</div>
		);
	}

	// show loading after PayPal order only
	if (loading && !orderData.creditCardNumber) {
		return <Loading />;
	}

	return (
		<section className='payment-info'>
			<StepHeader isOneLineCheckout={isOneLineCheckout} number={3} text='Pay With Card' showCards={true} />

			<div className='form'>
				<div className='form__field'>
					<NumberFormat
						format='#### #### #### ####'
						name='creditCardNumber'
						value={orderData.creditCardNumber}
						onChange={onFieldUpdate}
						placeholder='Credit Card Number'
						className={formErrors.creditCardNumber && 'error'}
					/>
					<i
						data-tip='All transactions are secured and encrypted'
						className='fas fa-lock form__field-icon'
					></i>
					<ReactTooltip />
					{formErrors.creditCardNumber && <div className='form__error'>{formErrors.creditCardNumber}</div>}
				</div>
				<div className='form__group form__group--one-line'>
					<div className='form__field'>
						<select onChange={(e) => setExpMonth(e.target.value)} value={expMonth} name='Expiration month'>
							{months.map((item, index) => (
								<option key={index} value={item.value}>
									{item.label}
								</option>
							))}
						</select>
						{formErrors.expDateError && <div className='form__error'>{formErrors.expDateError}</div>}
					</div>
					<div className='form__field'>
						<select onChange={(e) => setExpYear(e.target.value)} name='Expiration year' value={expYear}>
							{years.map((item, index) => (
								<option key={index} value={item.value}>
									{item.label}
								</option>
							))}
						</select>
					</div>
					<div className='form__field'>
						<NumberFormat
							format='####'
							placeholder='CVV'
							value={orderData.CVV}
							name='CVV'
							onChange={onFieldUpdate}
							className={formErrors.CVV && 'error'}
						/>
						<i
							data-tip={`<img src="${cvvImage}" style="min-width: 250px;" />`}
							className='far fa-question-circle form__field-icon'
							data-html={true}
						></i>
						<ReactTooltip />
						{formErrors.CVV && <div className='form__error'>{formErrors.CVV}</div>}
					</div>
				</div>
			</div>
			{isOneProductCheckout && crossSells.length > 0 && (
				<CrossSells
					onAdd={onCrossSellAdd}
					crossSells={crossSells}
					upsellProductIds={orderData.upsellProductIds.split(',')}
					isOneProductCheckout={isOneProductCheckout}
				/>
			)}

			{renderProtectionPackage()}
			{renderBillingForm()}
			<div className='form'>
				<Checkbox
					label={
						<div>
							I agree to the &nbsp;
							<span className='link' onClick={() => onTermsClick()}>
								Terms & Conditions
							</span>{' '}
							and{' '}
							<span className='link' onClick={() => onPrivacyClick()}>
								Privacy Policy
							</span>{' '}
							of this sale
						</div>
					}
					id='tc'
					checked={agreeTerms}
					onChange={() => setAgreeTerms(!agreeTerms)}
				/>
				{(formErrors.agreeTerms || agreeTermsError) && (
					<div className='form__error'>{formErrors.agreeTerms || agreeTermsError}</div>
				)}
			</div>

			<button className='big-green-btn' onClick={() => handleSubmit()}>
				COMPLETE MY ORDER
				{loading ? <i className='fas fa-spinner fa-spin'></i> : <i className='fas fa-chevron-right'></i>}
			</button>

			{queryParams.paypal === '1' && (
				<button
					className='big-green-btn big-green-btn--paypal'
					onClick={() => {
						if (!document.querySelector('#tc').checked) {
							setAgreeTermsError('You must agree to terms and conditions if you want to proceed');
						} else {
							setAgreeTermsError(false);
							onSubmitStep(preparePaymentInfo(true), {}, 4);
						}
					}}
				>
					<img
						src='data:image/svg+xml;base64,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'
						data-v-b01da731=''
						alt=''
						role='presentation'
						className='paypal-logo paypal-logo-paypal paypal-logo-color-blue'
					/>
				</button>
			)}
			{error && <div className='form__error text-center'>{error}</div>}
			{!isOneLineCheckout && (
				<button onClick={() => onPressBack(orderData)} className='go-back'>
					<i className='fas fa-chevron-left'></i> Go Back
				</button>
			)}
			<ShipsInfo />
			{showWebtvDisclaimer && (
				<p style={{ padding: '0 20px 20px', fontSize: '13px', lineHeight: 'normal' }}>{webtvDisclaimer}</p>
			)}
		</section>
	);
}

PaymentInfo.propTypes = {
	onPressBack: PropTypes.func,
	onSubmit: PropTypes.func,
	orderData: PropTypes.object,
};
